import { Alert, Flex, Form, Typography } from "antd";
import { NATIVE_TOKENS_ENUM, NETWORK_TEXT, NetworkType, NOTIFICATION_TYPE_ENUM } from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useCallback, useMemo, useState } from "react";
import { addWalletsToWhitelist } from "services";
import { useStores } from "stores";
import { CustomButton, DefaultSelect, DefaultTextArea, getCryptoIconRound } from "ui";
import { _isRequired, parseErrorMessage, parseInputList, renderSelectOptions, requestWithNotify } from "utils";
import { getTextareaRows } from "utils/whiteList";

export const AddWalletsModal = observer(() => {
  const { actionModals, walletsWhitelist } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [form] = Form.useForm();
  const destinationWalletsValue = Form.useWatch("destinationWallets", form);
  const selectedNetwork = Form.useWatch("network", form);
  const textareaRows = useMemo(() => getTextareaRows(destinationWalletsValue?.length ?? 0), [destinationWalletsValue]);

  const disabled = !(destinationWalletsValue && destinationWalletsValue.length && selectedNetwork);

  const formSubmit = useCallback(async () => {
    const parsedWalletsValue = parseInputList(destinationWalletsValue);

    setLoading(true);
    await walletsWhitelist
      .addWalletsToWhitelist(parsedWalletsValue, selectedNetwork)
      .then((response) => {
        setSuccessMsg(
          walletsWhitelist.walletAddress.length > 1
            ? `Добавлено адресов: ${walletsWhitelist.walletAddress.length}`
            : `Адрес ${walletsWhitelist.walletAddress[0]} добавлен в список`
        );
        setErrorMsg(null);
        setTimeout(() => {
          handleCloseModal();
        }, 500);
      })
      .catch((error) => {
        setErrorMsg(parseErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [destinationWalletsValue, selectedNetwork]);

  const handleCloseModal = async () => {
    await walletsWhitelist.fetch();
    form.resetFields();
    actionModals.closeAll();
  };

  return (
    <Flex vertical gap="md">
      <Form form={form} name="addWallets" onFinish={formSubmit}>
        <Flex vertical>
          <Form.Item name="network" className="full-width" rules={[_isRequired("Выберите сеть")]}>
            <DefaultSelect placeholder="Сеть" size="large">
              {renderSelectOptions(NETWORK_TEXT, (key) => getCryptoIconRound(key))}
            </DefaultSelect>
          </Form.Item>
          <Flex style={{ position: "relative" }}>
            <Form.Item name="destinationWallets" className="full-width" rules={[_isRequired("Введите адрес")]}>
              <DefaultTextArea placeholder="Адреса кошельков" size="large" rows={textareaRows} disabled={loading} />
            </Form.Item>
            <Typography.Text className="text-description text-xs" style={{ position: "absolute", bottom: 0, left: 0 }}>
              *Можно ввести несколько адресов через запятую, пробел или перенос строки
            </Typography.Text>
          </Flex>
        </Flex>
        <Form.Item className="margin-top-md">
          <Flex gap="small" align="center">
            <CustomButton
              type="primary"
              htmlType="submit"
              className="full-width"
              disabled={disabled || loading}
              loading={loading}
            >
              Добавить
            </CustomButton>
            <CustomButton
              variant="outlined"
              htmlType="button"
              className="full-width"
              onClick={handleCloseModal}
              disabled={loading}
              loading={loading}
            >
              Закрыть
            </CustomButton>
          </Flex>
        </Form.Item>
      </Form>

      {successMsg && <Alert message={successMsg} type="success" showIcon />}
      {errorMsg && <Alert message={errorMsg} type="error" showIcon />}
    </Flex>
  );
});
